import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManagePage = () => {
  const { subId, token } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/manage/${subId}/${token}`);
      setSub(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Link invalid or subscription not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/subscription/manage/${subId}/${token}/cancel`);
      toast.success('Auto-renewal canceled');
      setConfirming(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/subscription/manage/${subId}/${token}/reactivate`);
      toast.success('Auto-renewal restored');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reactivate');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link unavailable</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <p className="text-sm text-slate-500">If you need help, reply to any Audiostride email and we'll assist directly.</p>
        </div>
      </div>
    );
  }

  const price = Number(sub.price_usd || 0);
  const interval = sub.billing_interval === 'yearly' ? 'year' : 'month';
  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—';
  const canceled = sub.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden" data-testid="subscription-manage-page">
          <div className="bg-gradient-to-r from-teal-500 to-blue-600 text-white p-8">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-7 h-7" />
              <span className="text-sm uppercase font-bold tracking-wider text-teal-50">
                Audiostride · Manage Subscription
              </span>
            </div>
            <h1 className="text-3xl font-black mb-1">{sub.org_name}</h1>
            <p className="text-teal-50 text-lg">Hello {sub.contact_name}.</p>
          </div>

          <div className="p-8 space-y-6">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Row label="Plan" value={cap(sub.tier)} />
                <Row label="Price" value={`$${price.toFixed(0)} / ${interval}`} />
                <Row label="Report cadence" value={cap(sub.report_cadence)} />
                <Row label="Recipients" value={`Up to ${sub.max_recipients}`} />
                <Row label="Tour" value={sub.tour_title} span />
                <Row label="Next billing" value={periodEnd} />
                <Row label="Status" value={canceled ? 'Ending at period end' : 'Active · auto-renewing'} />
              </div>
            </div>

            {canceled ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="canceled-state">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-amber-900">Auto-renewal canceled.</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Your reports and access continue through <strong>{periodEnd}</strong>. You won't be charged again. Changed your mind? You can restore auto-renewal any time before then.
                    </p>
                  </div>
                </div>
                <button
                  onClick={reactivate}
                  disabled={busy}
                  data-testid="reactivate-btn"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  {busy ? 'Restoring…' : 'Restore auto-renewal'}
                </button>
              </div>
            ) : confirming ? (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-5" data-testid="confirm-cancel">
                <p className="font-bold text-rose-900">Cancel auto-renewal?</p>
                <p className="text-sm text-rose-800 mt-1">
                  Your reports continue through <strong>{periodEnd}</strong>, then billing stops. You can restore auto-renewal any time before then.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={cancel}
                    disabled={busy}
                    data-testid="confirm-cancel-btn"
                    className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm disabled:opacity-50"
                  >
                    {busy ? 'Canceling…' : 'Yes, cancel auto-renewal'}
                  </button>
                  <button
                    onClick={() => setConfirming(false)}
                    disabled={busy}
                    data-testid="back-btn"
                    className="px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
                  >
                    Keep subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => setConfirming(true)}
                  data-testid="cancel-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 hover:bg-rose-50 text-rose-700 font-bold text-sm border border-slate-200 hover:border-rose-200"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel auto-renewal
                </button>
              </div>
            )}

            <p className="text-xs text-slate-500 text-center pt-2">
              Need to add recipients or change your plan? Just reply to any Audiostride email — a real human will handle it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, span }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="text-slate-900 font-bold mt-0.5">{value}</div>
  </div>
);

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

export default SubscriptionManagePage;
